/**
 * Portfolio page: Systems, Craft, Intelligence sections and nav.
 * Content is loaded from /markdown/{section}.md files and parsed at runtime.
 * Supports toggling between the rendered panel view and a styled markdown source view.
 */

import { parseMarkdown, extractFrontmatter } from './markdown-parser.js';
import { renderMarkdownSource } from './markdown-viewer.js';
import { typewriteMarkdownSource } from './typewriter.js';
import { PANEL_ANIMATION_DELAY_MS, PLACEHOLDER_PANEL_COUNT } from './constants.js';
import { componentRegistry } from './components/index.js';
import { getPortfolioSectionFromPath } from './routing.js';

// Cache of raw markdown text per section, populated as sections load
const markdownCache = {};

// Currently active section
let activeSection = 'systems';

// Whether the markdown source view is showing
let markdownViewActive = false;

function createPortfolioVisualItem(panel, index) {
  const { image: imagePath, alt: altText, noBorder, mobileOnly } = panel;
  const itemDiv = document.createElement('div');
  itemDiv.className = 'portfolio-panel' +
    (noBorder ? ' portfolio-panel--no-border' : '') +
    (mobileOnly ? ' portfolio-panel--mobile-only' : '');
  itemDiv.classList.add('skeleton-loading');

  const img = document.createElement('img');
  img.alt = altText || 'Portfolio image';

  img.onload = () => itemDiv.classList.remove('skeleton-loading');
  img.onerror = () => {
    img.src = 'https://placehold.co/800x600/27272a/ffffff?text=Image+Missing';
    itemDiv.classList.remove('skeleton-loading');
  };
  img.src = imagePath;

  setTimeout(() => itemDiv.classList.add('animated'), PANEL_ANIMATION_DELAY_MS * (index || 0));
  itemDiv.appendChild(img);
  return itemDiv;
}

function createPortfolioTitleItem(text) {
  const el = document.createElement('h1');
  el.className = 'portfolio-page-title';
  el.textContent = text;
  return el;
}

function createPortfolioTextItem(headline, body) {
  const itemDiv = document.createElement('div');
  itemDiv.className = 'portfolio-panel portfolio-panel-text';
  const headlineEl = document.createElement('div');
  headlineEl.className = 'portfolio-panel-headline';
  headlineEl.textContent = headline;
  const bodyEl = document.createElement('div');
  bodyEl.className = 'portfolio-panel-body';
  bodyEl.textContent = body;
  itemDiv.appendChild(headlineEl);
  itemDiv.appendChild(bodyEl);
  setTimeout(() => itemDiv.classList.add('animated'), PANEL_ANIMATION_DELAY_MS);
  return itemDiv;
}

function createPortfolioPlaceholderItem(section, index) {
  return createPortfolioVisualItem({ image: section, alt: `${section} project ${index}` }, index);
}

function renderSectionMeta(metaEl, meta) {
  if (!metaEl || !meta) return;
  metaEl.innerHTML = '';
  ['product', 'category', 'team'].forEach((key) => {
    if (!meta[key]) return;
    const item = document.createElement('div');
    item.className = 'portfolio-meta-item';
    const label = document.createElement('span');
    label.className = 'portfolio-meta-label';
    label.textContent = key.charAt(0).toUpperCase() + key.slice(1);
    let value;
    if (key === 'product' && meta.url) {
      value = document.createElement('a');
      value.href = meta.url;
      value.target = '_blank';
      value.rel = 'noopener noreferrer';
      value.className = 'portfolio-meta-value portfolio-meta-link';
    } else {
      value = document.createElement('span');
      value.className = 'portfolio-meta-value';
    }
    value.textContent = meta[key];
    item.appendChild(label);
    item.appendChild(value);
    metaEl.appendChild(item);
  });
}

function renderSectionToGrid(gridEl, panels, sectionName) {
  if (!gridEl) return;
  gridEl.innerHTML = '';

  if (panels?.length > 0) {
    panels.forEach((panel, index) => {
      if (panel.type === 'title') {
        gridEl.appendChild(createPortfolioTitleItem(panel.text));
      } else if (panel.type === 'visual') {
        gridEl.appendChild(createPortfolioVisualItem(panel, index));
      } else if (panel.type === 'text') {
        gridEl.appendChild(createPortfolioTextItem(panel.headline, panel.body));
      } else if (panel.type === 'component') {
        const factory = componentRegistry[panel.name];
        if (factory) {
          const el = factory(panel.props, index);
          if (el) {
            if (panel.noBorder) el.classList.add('portfolio-panel--no-border');
            gridEl.appendChild(el);
          }
        }
      }
    });
  } else {
    for (let i = 0; i < PLACEHOLDER_PANEL_COUNT; i++) {
      gridEl.appendChild(createPortfolioPlaceholderItem(sectionName, i + 1));
    }
  }
}

function showMarkdownSource() {
  const text = markdownCache[activeSection] || '';
  const sourceEl = document.getElementById('portfolioMarkdownSource');
  if (sourceEl) typewriteMarkdownSource(text, sourceEl, renderMarkdownSource);
}

function setMarkdownView(active) {
  markdownViewActive = active;

  const container = document.getElementById('portfolioSectionContainer');
  const sourceEl = document.getElementById('portfolioMarkdownSource');
  const toggleBtn = document.getElementById('toggleMarkdownBtn');
  const mdActions = document.getElementById('mdActions');

  // Cancel any in-flight glitch animation so the label updates instantly
  toggleBtn?._glitchCancel?.();

  if (active) {
    container?.classList.add('page-hidden');
    sourceEl?.classList.remove('page-hidden');
    mdActions?.classList.remove('page-hidden');
    if (toggleBtn) {
      toggleBtn.textContent = 'Rendered';
      toggleBtn.dataset.glitchLabel = 'Rendered';
    }
    showMarkdownSource();
  } else {
    container?.classList.remove('page-hidden');
    sourceEl?.classList.add('page-hidden');
    mdActions?.classList.add('page-hidden');
    if (toggleBtn) {
      toggleBtn.textContent = 'Markdown';
      toggleBtn.dataset.glitchLabel = 'Markdown';
    }
  }
}

function initializeToolbar() {
  document.getElementById('toggleMarkdownBtn')?.addEventListener('click', () => {
    setMarkdownView(!markdownViewActive);
  });

  document.getElementById('mdCopyBtn')?.addEventListener('click', async () => {
    const text = markdownCache[activeSection] || '';
    await navigator.clipboard.writeText(text);
    const btn = document.getElementById('mdCopyBtn');
    if (btn) {
      btn.textContent = 'Copied!';
      setTimeout(() => { btn.textContent = 'Copy'; }, 2000);
    }
  });

  document.getElementById('mdDownloadBtn')?.addEventListener('click', () => {
    const text = markdownCache[activeSection] || '';
    const blob = new Blob([text], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${activeSection}.md`;
    a.click();
    URL.revokeObjectURL(url);
  });
}

function showSection(targetSection) {
  activeSection = targetSection;

  const sections = document.querySelectorAll('.portfolio-section-content');
  const popoverLinks = document.querySelectorAll('.nav-popover-section[data-section]');

  popoverLinks.forEach((l) => l.classList.toggle('active', l.getAttribute('data-section') === targetSection));
  sections.forEach((section) => {
    section.style.display = section.getAttribute('data-section') === targetSection ? 'block' : 'none';
  });

  // If already in markdown view, update the source for the new section
  if (markdownViewActive) showMarkdownSource();

  window.scrollTo(0, 0);
}

function initializePortfolioNavigation(initialSection) {
  document.addEventListener('portfolio-section-change', (e) => {
    const { section } = e.detail || {};
    if (section) showSection(section);
  });
  showSection(initialSection || 'systems');

  document.getElementById('backToTopBtn')?.addEventListener('click', () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });
}

/**
 * Fetch, cache, and parse a section's markdown file.
 * @param {string} sectionName
 * @returns {Promise<Array>} panels array
 */
async function loadSectionFromMarkdown(sectionName) {
  const res = await fetch(`/markdown/${sectionName}.md`);
  if (!res.ok) return { panels: [], meta: {} };
  const text = await res.text();
  markdownCache[sectionName] = text;
  const { meta, body } = extractFrontmatter(text);
  return { panels: parseMarkdown(body), meta };
}

async function loadPortfolioSections() {
  const [systems, craft, intelligence] = await Promise.all([
    loadSectionFromMarkdown('systems'),
    loadSectionFromMarkdown('craft'),
    loadSectionFromMarkdown('intelligence'),
  ]);

  renderSectionMeta(document.getElementById('systemsMeta'), systems.meta);
  renderSectionMeta(document.getElementById('craftMeta'), craft.meta);
  renderSectionMeta(document.getElementById('intelligenceMeta'), intelligence.meta);

  renderSectionToGrid(document.getElementById('systemsGrid'), systems.panels, 'Systems');
  renderSectionToGrid(document.getElementById('craftGrid'), craft.panels, 'Craft');
  renderSectionToGrid(document.getElementById('intelligenceGrid'), intelligence.panels, 'Intelligence');
}

/**
 * Initialize portfolio page: Systems, Craft, Intelligence sections.
 * Loads content from /markdown/*.md files, sets up section nav and source toggle.
 */
export function initializePortfolioPage() {
  const initialSection = getPortfolioSectionFromPath(location.pathname);
  initializePortfolioNavigation(initialSection);
  initializeToolbar();
  loadPortfolioSections();
}
